// @flow
import React, { useContext, useEffect } from 'react';
import classnames from 'classnames';

import { BEFORE_APP } from 'Components/frameworks/portal/Portal.constants';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { ID_LAYER_HEADER__SHOPPING__PRICE_REMOTE_DETAILS } from 'Components/utils/identifiers/constants';

import { Layer } from 'Components/frameworks/layer/Layer';
import { PriceRemoteServiceList } from 'Components/widgets/price-remote/price-remote-service-list/PriceRemoteServiceList';
import { PriceRemoteContext } from 'Components/widgets/price-remote/PriceRemoteContext';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { type Service } from 'Components/widgets/product-services/product-service/ProductService';

type Props = {
  allInclusiveServices: $ReadOnlyArray<Service>,
  closeLabel?: string,
  isLoading?: boolean,
  nameRender?: string,
  onBack: () => void,
  children: React$Node,
};

const REMOTE_HEIGHT = '250px';

export const PriceRemoteDetailsLayer = ({
  allInclusiveServices,
  closeLabel,
  isLoading,
  onBack,
  nameRender,
  children,
}: Props) => {
  const [{ isTotalPrice }, setPriceRemoteContext] = useContext(PriceRemoteContext);

  useEffect(() => {
    setPriceRemoteContext({ isInLayer: true });
    return () => {
      setPriceRemoteContext({ isInLayer: false });
    };
  }, []);

  const layerHeaderTitle = t('priceRemote.openAllInclusiveDetails');

  return (
    <Layer
      ariaLabel={layerHeaderTitle}
      ariaLabelledby={ID_LAYER_HEADER__SHOPPING__PRICE_REMOTE_DETAILS}
      buttonLeft={{
        label: t('common.backNavigation'),
        iconName: ICONS.LEFT_ARROW,
        onClick: onBack,
      }}
      closeLabel={closeLabel}
      hasAnimateEnter
      header={
        <span
          id={ID_LAYER_HEADER__SHOPPING__PRICE_REMOTE_DETAILS}
          className="w-full text-center block"
        >
          {layerHeaderTitle}
        </span>
      }
      onClose={onBack}
      portalName={nameRender || BEFORE_APP}
    >
      <div
        className={classnames('flex flex-col px-5 items-center scroll-y transition-opacity', {
          'opacity-25': isLoading,
        })}
        style={{ height: `calc(100% - (${REMOTE_HEIGHT} - 60px))` }}
        data-testid="price-remote-details-layer-container"
      >
        {isTotalPrice ? (
          <div className="text-base text-blue font-bold text-center pt-4 pb-1 lg:pt-2">
            {t('priceRemote.priceForAll')}
          </div>
        ) : null}
        {allInclusiveServices && allInclusiveServices.length > 0 ? (
          <PriceRemoteServiceList services={allInclusiveServices} isDesktop={false} />
        ) : (
          <Spinner size="small" />
        )}
      </div>
      {children}
    </Layer>
  );
};
